/**
 * Utility functions for date formatting
 */

/**
 * Formats a date string to a localized date string
 * Handles various date formats including Supabase timestamps
 * @param {string} dateString - The date string to format
 * @returns {string} - Formatted date string or 'Invalid Date' fallback
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  
  try {
    // Handle Supabase timestamp format: 2025-07-14 13:57:53.043574+00
    // Convert to ISO format if needed
    let isoString = dateString;
    
    // If it's a Supabase timestamp format, convert to proper ISO format
    if (typeof dateString === 'string' && dateString.includes(' ') && dateString.includes('+')) {
      // Replace space with 'T' and ensure proper timezone format
      isoString = dateString.replace(' ', 'T');
      // Ensure timezone has colon (e.g., +00:00 instead of +00)
      if (isoString.endsWith('+00')) {
        isoString = isoString.replace('+00', '+00:00');
      }
    }
    
    const date = new Date(isoString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return 'Invalid Date';
  }
};

/**
 * Formats a date string to a localized date and time string
 * @param {string} dateString - The date string to format
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'No date';
  
  try {
    // Handle Supabase timestamp format
    let isoString = dateString;
    
    if (typeof dateString === 'string' && dateString.includes(' ') && dateString.includes('+')) {
      isoString = dateString.replace(' ', 'T');
      if (isoString.endsWith('+00')) {
        isoString = isoString.replace('+00', '+00:00');
      }
    }
    
    const date = new Date(isoString);
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Invalid Date';
    }
    
    return date.toLocaleString();
  } catch (error) {
    console.error('Error formatting datetime:', error, dateString);
    return 'Invalid Date';
  }
};

/**
 * Formats a date string to a relative time string (e.g., "2 hours ago")
 * @param {string} dateString - The date string to format
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'No date';
  
  try {
    // Handle Supabase timestamp format
    let isoString = dateString;
    
    if (typeof dateString === 'string' && dateString.includes(' ') && dateString.includes('+')) {
      isoString = dateString.replace(' ', 'T');
      if (isoString.endsWith('+00')) {
        isoString = isoString.replace('+00', '+00:00');
      }
    }
    
    const date = new Date(isoString);
    
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return formatDate(dateString);
  } catch (error) {
    console.error('Error formatting relative time:', error, dateString);
    return 'Invalid Date';
  }
}; 